import styled, { keyframes } from 'styled-components'

const neonPulse = keyframes`
  0%, 100% { text-shadow: 0 0 10px rgba(0,229,255,0.6), 0 0 30px rgba(0,229,255,0.3); }
  50%       { text-shadow: 0 0 20px rgba(0,229,255,0.9), 0 0 50px rgba(0,229,255,0.5); }
`

/* ── Page wrapper ── */
export const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: #02e5ff;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding-bottom: 80px;
  font-family: inherit;
`

/* ── Hero / Top section ── */
export const TopSection = styled.div`
  padding: 40px 3% 30px;
`

export const PageTitle = styled.h1`
  color: #fff;
  font-size: 2.6rem;
  font-weight: 900;
  font-style: italic;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 0 0 30px 0;
  animation: ${neonPulse} 3s ease-in-out infinite;

  @media (max-width: 600px) {
    font-size: 1.8rem;
  }
`

export const TopGrid = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;

  @media (max-width: 850px) {
    flex-direction: column;
  }
`

/* Left column */
export const LeftCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 150px;
  max-width: 180px;
  padding-top: 6px;

  @media (max-width: 850px) {
    max-width: 100%;
    flex-direction: row;
    flex-wrap: wrap;
  }
`

export const LeftSubtitle = styled.p`
  color: #aaa;
  font-size: 0.82rem;
  margin: 0 0 6px 0;
  line-height: 1.4;
`

export const PinkBtn = styled.a`
  display: inline-block;
  background: linear-gradient(135deg, #ff0066, #ff3399);
  color: #fff;
  padding: 10px 20px;
  border-radius: 26px;
  font-weight: 800;
  font-size: 0.85rem;
  text-transform: uppercase;
  text-decoration: none;
  letter-spacing: 1.5px;
  cursor: pointer;
  box-shadow: 0 0 14px rgba(255, 0, 102, 0.6);
  transition: transform 0.2s, box-shadow 0.2s;
  text-align: center;

  &:hover {
    transform: scale(1.04);
    box-shadow: 0 0 22px rgba(255, 0, 102, 0.8);
  }
`

export const BlueBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background-color: #0055ff;
  color: #fff;
  padding: 10px 20px;
  border-radius: 26px;
  font-weight: 700;
  font-size: 0.85rem;
  text-transform: uppercase;
  text-decoration: none;
  letter-spacing: 1px;
  cursor: pointer;
  box-shadow: 0 0 12px rgba(0, 85, 255, 0.5);
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.04);
  }
`

/* Right — 3 service cards */
export const CardsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  flex: 1;
  margin-left: auto;

  @media (max-width: 700px) {
    flex-direction: column;
    align-items: flex-end;
  }
`

export const ServiceCard = styled.div`
  flex: 1;
  border: 2px solid #fc0a7e;
  border-radius: 25px;
  padding: 20px 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  background: #02e5ff;
  box-shadow: 0 0 20px rgba(252, 10, 126, 0.4), inset 0 0 15px rgba(252, 10, 126, 0.1);
  text-align: center;
  transition: box-shadow 0.3s, transform 0.3s;
  cursor: default;
  max-width: 280px;

  &:hover {
    box-shadow: 0 0 22px rgba(252, 10, 126, 0.55);
    transform: translateY(-3px);
  }

  img {
    width: 60px;
    height: 60px;
    object-fit: contain;
    filter: drop-shadow(0 0 6px rgba(10, 159, 252, 0.7));
  }
`

export const ServiceCardTitle = styled.h3`
  color: #fff;
  font-size: 1rem;
  font-weight: 800;
  font-style: italic;
  margin: 0;
  text-align: center;
  text-shadow: 0 0 8px rgba(252, 10, 126, 0.4);
`

export const ServiceCardText = styled.p`
  color: #fff;
  font-size: 0.78rem;
  margin: 0 auto;
  line-height: 1.45;
  width: 60%;
`

/* ── Contact Form Section ── */
export const FormSection = styled.div`
  padding: 10px 3% 30px;
`

export const FormSectionTitle = styled.h2`
  color: #fff;
  font-style: italic;
  font-weight: 800;
  font-size: 1.3rem;
  margin: 0 0 20px 0;
`

export const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export const FormRow = styled.div`
  display: flex;
  gap: 22px;
  margin-bottom: 22px;

  @media (max-width: 700px) {
    flex-direction: column;
  }
`

export const FormInput = styled.input`
  flex: 1;
  background: transparent;
  border: 1.5px solid #00e5ff;
  border-radius: 25px;
  color: #fff;
  padding: 15px 26px;
  font-size: 0.85rem;
  outline: none;

  &::placeholder {
    color: #666;
  }

  &:focus {
    border-color: #00e5ff;
    box-shadow: 0 0 14px rgba(0, 229, 255, 0.35);
  }
`

export const FormTextarea = styled.textarea`
  flex: 1;
  background: transparent;
  border: 1.5px solid #00e5ff;
  border-radius: 18px;
  color: #fff;
  padding: 13px 16px;
  font-size: 0.85rem;
  outline: none;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;

  &::placeholder {
    color: #666;
  }

  &:focus {
    border-color: #00e5ff;
    box-shadow: 0 0 14px rgba(0, 229, 255, 0.35);
  }
`

export const FormFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  flex-wrap: wrap;
  gap: 12px;
`

export const FormNote = styled.span`
  color: #fff;
  font-size: 0.8rem;
  font-style: italic;
  text-shadow: 0 0 8px rgba(0, 229, 255, 0.6);
`

export const SendBtn = styled.button`
  background: linear-gradient(135deg, #ff0066, #ff3399);
  color: #fff;
  border: none;
  padding: 12px 35px;
  border-radius: 50px;
  font-weight: 800;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  cursor: pointer;
  box-shadow: 0 0 16px rgba(255, 0, 102, 0.6);
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 0 26px rgba(255, 0, 102, 0.8);
  }
`

/* ── Support Options ── */
export const SupportSection = styled.div`
  padding: 0 3% 30px;
`

export const SupportTitle = styled.h2`
  color: #fff;
  font-style: italic;
  font-weight: 800;
  font-size: 1.3rem;
  margin: 0 0 18px 0;
`

export const YellowBanner = styled.div`
  width: 100%;
  background-color: #fff000;
  padding: 30px 30px;
  border-radius: 12px;
  box-sizing: border-box;
  text-align: center;
`

export const BannerTitle = styled.h2`
  color: #02e5ff;
  font-weight: 900;
  font-size: 2rem;
  font-style: italic;
  margin: 0 0 8px 0;

  @media (max-width: 600px) {
    font-size: 1.4rem;
  }
`

export const BannerSubtitle = styled.p`
  color: #02e5ff;
  font-size: 0.88rem;
  margin: 0;
  font-weight: 500;
`

/* ── Sticky Bottom Bar ── */
export const StickyBar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 62px;
  background-color: #0055ff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  box-shadow: 0 -4px 20px rgba(0, 85, 255, 0.5);
  z-index: 100;
  gap: 10px;
`

export const StickyLeft = styled.span`
  color: #fff;
  font-size: 0.85rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  white-space: nowrap;

  @media (max-width: 600px) {
    font-size: 0.68rem;
    letter-spacing: 0.5px;
  }
`

export const StickyCenter = styled.span`
  color: #fff;
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  white-space: nowrap;

  @media (max-width: 768px) {
    display: none;
  }
`

export const StickyOrderBtn = styled.a`
  background-color: #fff000;
  color: #02e5ff;
  padding: 9px 20px;
  border-radius: 6px;
  font-weight: 800;
  font-size: 0.82rem;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.04);
  }
`
export const BottomBar = styled.div`
  width: 95%;
  margin: 0 auto 40px auto;
  background-color: #0056ff;
  padding: 10px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
  border-radius: 50px;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 15px;
    text-align: center;
    border-radius: 25px;
    padding: 20px;
  }
`

export const BottomText = styled.h2`
  color: #fff;
  font-style: italic;
  font-weight: bold;
  margin: 0;
  font-size: 1.5rem;
`

export const BottomBtn = styled.button`
  background-color: #fff000;
  color: #02e5ff;
  border: none;
  padding: 12px 30px;
  font-weight: bold;
  border-radius: 50px;
  font-size: 1.1rem;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05);
  }
`
